/**
 * Persona Resource
 *
 * A 1:1 mapping to the persona API. No default parameter injection — the SDK
 * never fills a field in for you.
 *
 * The one exception is a SHAPE guard on the two config-writing methods
 * (sdk ADR-037): they refuse an `agent_config` missing a field the backend
 * requires, synchronously and before the network. It adds no values and makes
 * no value judgements — which models and processes exist stays the backend's
 * sole authority.
 */

import type { HttpLayer } from '../http.js';
import { PersonaConfigIncompleteError } from '../errors.js';
import type { PersonaAgentConfigInput } from '../types/persona.js';
import type {
  DefaultOverridesResult,
  ParamSchema,
  Persona,
  PersonaGroup,
  PersonaConfig,
  PersonaOptionsResponse,
  SystemPersonaWithConfig,
  CreatePersonaGroupRequest,
  UpdatePersonaGroupRequest,
  DeletePersonaGroupRequest,
  CreatePersonaWithConfigRequest,
  CreateNewPersonaConfigRequest,
  UpdatePersonaRequest,
  DeletePersonaRequest,
  DeletePersonaResult,
  ListPersonasRequest,
  GetPersonaRequest,
  GetPersonaByNameRequest,
  GetPersonaConfigRequest,
  GetLatestPersonaConfigRequest,
  GetLatestPersonaConfigByNameRequest,
  CopySystemPersonaRequest,
  CopySystemPersonaResponse,
  PersonaOptionsUpdate,
  ListProvidersResponse,
  ListProviderModelsResponse,
  ModelCatalogEntry,
  CreateCatalogEntryParams,
  UpdateCatalogEntryParams,
  EntryReferencesResponse,
  ReassignEntryParams,
  ReassignEntryResponse,
  SyncResult,
  ProviderType,
  DiscoverProviderResult,
  ProviderCredentialStatus,
  ByokMode,
  VisionModelInfo,
} from '../types/index.js';

const blank = (value: unknown): boolean =>
  typeof value !== 'string' || value.trim() === '';

/**
 * Mirrors go/internal/persona/config_contract.go. Reports EVERY missing field,
 * never just the first (persona ADR-020).
 */
function assertCompleteAgentConfig(
  method: string,
  config: PersonaAgentConfigInput | undefined,
): void {
  if (config === undefined || config === null) {
    throw new PersonaConfigIncompleteError(method, ['agent_config is required']);
  }

  const missing: string[] = [];
  if (blank(config.provider_type)) missing.push('provider_type is required');
  if (blank(config.model_name)) missing.push('model_name is required');
  if (blank(config.system_prompt)) {
    missing.push('system_prompt is required — it is the agent’s role definition');
  }
  if (config.completion_params === undefined || config.completion_params === null) {
    missing.push('completion_params is required — use {} to inherit the model’s defaults');
  }

  if (config.automatic_process_selection === undefined) {
    missing.push('automatic_process_selection is required');
  }
  if (config.automatic_process_ids === undefined) {
    missing.push('automatic_process_ids is required');
  }

  // The disjunction the type cannot express: an explicit process, OR automatic
  // mode over a non-empty candidate set. Both empty is the shape that reached
  // agent_process/selection.go and failed there instead of here.
  const hasExplicit = !blank(config.agent_process_id);
  if (
    !hasExplicit &&
    config.automatic_process_selection !== undefined &&
    config.automatic_process_ids !== undefined
  ) {
    if (config.automatic_process_selection) {
      if (config.automatic_process_ids.length === 0) {
        missing.push('automatic mode needs at least one candidate');
      }
    } else {
      missing.push('name an agent_process_id or enable automatic selection');
    }
  }

  if (missing.length > 0) {
    throw new PersonaConfigIncompleteError(method, missing);
  }
}

export class PersonaResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/persona';

  // --- Options ---

  async getOptions(): Promise<PersonaOptionsResponse> {
    return this.http.get(`${this.prefix}/options`);
  }

  async setOptions(
    params: PersonaOptionsUpdate,
  ): Promise<PersonaOptionsResponse> {
    return this.http.put(`${this.prefix}/options`, params);
  }

  // --- Groups ---

  async listGroups(): Promise<PersonaGroup[]> {
    return this.http.post(`${this.prefix}/groups/list`, {});
  }

  async getGroup(
    params: { group_id: string },
  ): Promise<PersonaGroup> {
    return this.http.post(`${this.prefix}/groups/get`, params);
  }

  async getGroupByName(
    params: { name: string },
  ): Promise<PersonaGroup> {
    return this.http.post(`${this.prefix}/groups/get-by-name`, params);
  }

  async createGroup(
    params: CreatePersonaGroupRequest,
  ): Promise<PersonaGroup> {
    return this.http.post(`${this.prefix}/groups/create`, params);
  }

  async updateGroup(
    params: UpdatePersonaGroupRequest,
  ): Promise<PersonaGroup> {
    return this.http.post(`${this.prefix}/groups/update`, params);
  }

  async deleteGroup(params: DeletePersonaGroupRequest): Promise<void> {
    return this.http.post(`${this.prefix}/groups/delete`, params);
  }

  // --- Personas ---

  async listPersonas(
    params?: ListPersonasRequest,
  ): Promise<Persona[]> {
    return this.http.post(`${this.prefix}/personas/list`, params ?? {});
  }

  async getPersona(params: GetPersonaRequest): Promise<Persona> {
    return this.http.post(`${this.prefix}/personas/get`, params);
  }

  async getPersonaByName(
    params: GetPersonaByNameRequest,
  ): Promise<Persona> {
    return this.http.post(
      `${this.prefix}/personas/get-by-name`,
      params,
    );
  }

  /**
   * Create a persona and its first config in one call.
   *
   * `agent_config` must carry `provider_type`, `model_name`, `system_prompt`,
   * `completion_params` (`{}` inherits the model's defaults) and a process —
   * either `agent_process_id`, or `automatic_process_selection: true` with a
   * NON-EMPTY `automatic_process_ids`. An incomplete config is refused here,
   * before the request leaves the client.
   *
   * `fingerprint` is not a value to invent or to ask a user for: call
   * {@link PersonaResource.getParamSchema} with the SAME `provider_type` and
   * `model_name` the config names, and echo the `fingerprint` it returns. An
   * empty echo is a 422; a stale one is a 409 meaning the model's parameters
   * changed since you read them — read again and retry.
   *
   * Prefer {@link PersonaResource.copySystemPersona} when a catalog persona is
   * close to the role you want: the copy already carries a working provider and
   * model, so nothing has to be chosen.
   */
  async createPersonaWithConfig(
    params: CreatePersonaWithConfigRequest,
  ): Promise<PersonaConfig> {
    assertCompleteAgentConfig('createPersonaWithConfig', params.agent_config);
    return this.http.post(
      `${this.prefix}/personas/create-with-config`,
      params,
    );
  }

  async updatePersona(params: UpdatePersonaRequest): Promise<Persona> {
    return this.http.post(`${this.prefix}/personas/update`, params);
  }

  /**
   * Delete a persona. The delete is soft: the persona leaves the persona list and
   * starts no new Agents or chats, while its existing Agents keep running its last
   * configuration. With `delete_agents`, its Agents are deleted too and their
   * active work is stopped. The delete is recorded in the account's audit trail.
   * @param params `persona_id` and optional `delete_agents`.
   */
  async deletePersona(params: DeletePersonaRequest): Promise<DeletePersonaResult> {
    return this.http.post(`${this.prefix}/personas/delete`, params);
  }

  // --- Configs ---

  async getConfig(
    params: GetPersonaConfigRequest,
  ): Promise<PersonaConfig> {
    return this.http.post(`${this.prefix}/configs/get`, params);
  }

  /**
   * Add a new config version to an existing persona. Configs are append-only —
   * this supersedes the persona's latest config rather than editing it.
   *
   * `agent_config` must carry `provider_type`, `model_name`, `system_prompt`,
   * `completion_params` (`{}` inherits the model's defaults) and a process —
   * either `agent_process_id`, or `automatic_process_selection: true` with a
   * NON-EMPTY `automatic_process_ids`. An incomplete config is refused here,
   * before the request leaves the client.
   *
   * `fingerprint` is not a value to invent or to ask a user for: call
   * {@link PersonaResource.getParamSchema} with the SAME `provider_type` and
   * `model_name` the config names, and echo the `fingerprint` it returns. An
   * empty echo is a 422; a stale one is a 409 meaning the model's parameters
   * changed since you read them — read again and retry.
   */
  async createNewConfig(
    params: CreateNewPersonaConfigRequest,
  ): Promise<PersonaConfig> {
    assertCompleteAgentConfig('createNewConfig', params.agent_config);
    return this.http.post(
      `${this.prefix}/personas/create-config`,
      params,
    );
  }

  async getLatestConfig(
    params: GetLatestPersonaConfigRequest,
  ): Promise<PersonaConfig> {
    return this.http.post(
      `${this.prefix}/configs/get-latest`,
      params,
    );
  }

  async getLatestConfigByName(
    params: GetLatestPersonaConfigByNameRequest,
  ): Promise<PersonaConfig> {
    return this.http.post(
      `${this.prefix}/configs/get-latest-by-name`,
      params,
    );
  }

  // --- System Personas ---

  /**
   * The system persona catalog: every ready-made role persona this account may
   * copy, with its config. Read this before authoring a persona by hand — the
   * catalog covers most org roles, and copying one is how a new persona gets a
   * working provider and model without anything being chosen.
   */
  async listActiveSystemPersonas(): Promise<SystemPersonaWithConfig[]> {
    return this.http.get(`${this.prefix}/system-personas/active`);
  }

  /**
   * Copy a catalog persona from {@link PersonaResource.listActiveSystemPersonas}
   * into this account under `new_name`, returning the new `persona_id`.
   *
   * The copy carries the catalog entry's provider, model and completion_params,
   * so it is runnable as-is. To make it a specific role, read it back with
   * {@link PersonaResource.getLatestConfig}, change only `system_prompt` and
   * `agent_process_id`, and write it with
   * {@link PersonaResource.createNewConfig}.
   */
  async copySystemPersona(
    params: CopySystemPersonaRequest,
  ): Promise<CopySystemPersonaResponse> {
    return this.http.post(
      `${this.prefix}/personas/copy-system`,
      params,
    );
  }

  // --- Account Model Catalog ---
  // Entry names / model IDs may contain slashes (fireworks), so they travel
  // in bodies and URL-encoded query params — never path segments.

  async listProviders(): Promise<ListProvidersResponse> {
    return this.http.get(`${this.prefix}/providers`);
  }

  async listProviderModels(
    providerType: string,
  ): Promise<ListProviderModelsResponse> {
    return this.http.get(`${this.prefix}/providers/${providerType}/models`);
  }

  /**
   * Which model reads images for this account, and whether it can actually run.
   *
   * The image-reading model is one stack-wide platform choice, so this read has
   * no setter: there is deliberately no way to change it from the product.
   * `eligible` is false when the model is not marked vision-capable or its
   * provider adapter cannot send image parts, and `ineligible_reason` names
   * which. `key_policy_blocks` is the separate case where the model is fine but
   * the account's own-keys-only policy for that provider has no key stored — the
   * agent's `image_read` tool reports the same condition, from the same
   * computation, so settings and chat cannot disagree.
   */
  async getVisionModel(): Promise<VisionModelInfo> {
    return this.http.get(`${this.prefix}/vision-model`);
  }

  /**
   * The ONE schema a client renders from.
   *
   * The backend resolves this with the same function its three write guards use,
   * which is what makes "the client and the server cannot disagree" true rather
   * than aspirational — they run one implementation over the same rows.
   *
   * Hold the returned `fingerprint` and echo it on every config write. A stale
   * echo is a 409, so the user is told "the parameters changed since you opened
   * this editor — reload" instead of being handed a confusing param-level
   * rejection naming a control they never touched.
   */
  async getParamSchema(
    providerType: string,
    modelName: string,
  ): Promise<ParamSchema> {
    const query = new URLSearchParams({ provider_type: providerType, model_name: modelName });
    return this.http.get(`${this.prefix}/param-schema?${query.toString()}`);
  }

  /**
   * Write provider-tier default VALUES — user-owned, and separate from the
   * discovery-owned capability spec.
   *
   * Previously these were the same write through the same route, and discovery
   * OWNED the blob: a sync overwrote `supported_params` wholesale, silently
   * erasing a tuned default. `default_params` survives a sync by construction.
   *
   * Returns the RE-RESOLVED schema, so the caller's next save echoes a current
   * fingerprint rather than the one it just invalidated.
   */
  async setProviderParamDefaults(
    providerType: string,
    defaults: Record<string, unknown>,
    fingerprint: string,
  ): Promise<ParamSchema> {
    return this.http.put(`${this.prefix}/providers/${providerType}/defaults`, {
      defaults,
      fingerprint,
    });
  }

  /** Write model-tier default VALUES. Overrides the provider tier per key. */
  async setModelParamDefaults(
    providerType: string,
    modelName: string,
    defaults: Record<string, unknown>,
    fingerprint: string,
  ): Promise<ParamSchema> {
    return this.http.put(
      `${this.prefix}/providers/${providerType}/models/${encodeURIComponent(modelName)}/defaults`,
      { defaults, fingerprint },
    );
  }

  /**
   * Which agents PIN these params, and will therefore NOT receive a defaults
   * change.
   *
   * Under sparse pins that is the whole blast radius: an unpinned agent picks the
   * new value up on its next turn, so the interesting set is exactly the ones
   * that will not. Call it BEFORE committing a defaults write — the point is that
   * the user learns the impact while they can still decide.
   *
   * `modelName` is optional. Omitting it asks about every model on the provider,
   * which is what a provider-tier change actually affects.
   */
  async listDefaultOverrides(
    providerType: string,
    params: string[],
    modelName?: string,
  ): Promise<DefaultOverridesResult> {
    const query = new URLSearchParams({ params: params.join(',') });
    if (modelName) query.set('model_name', modelName);
    return this.http.get(
      `${this.prefix}/providers/${providerType}/defaults/impact?${query.toString()}`,
    );
  }

  async createCatalogEntry(
    providerType: string,
    params: CreateCatalogEntryParams,
  ): Promise<ModelCatalogEntry> {
    return this.http.post(
      `${this.prefix}/providers/${providerType}/models`,
      params,
    );
  }

  async updateCatalogEntry(
    providerType: string,
    params: UpdateCatalogEntryParams,
  ): Promise<ModelCatalogEntry> {
    return this.http.put(
      `${this.prefix}/providers/${providerType}/models`,
      params,
    );
  }

  async deleteCatalogEntry(
    providerType: string,
    name: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix}/providers/${providerType}/models?name=${encodeURIComponent(name)}`,
    );
  }

  async listEntryReferences(
    providerType: string,
    name: string,
  ): Promise<EntryReferencesResponse> {
    return this.http.get(
      `${this.prefix}/providers/${providerType}/models/references?name=${encodeURIComponent(name)}`,
    );
  }

  async reassignEntryReferences(
    providerType: string,
    params: ReassignEntryParams,
  ): Promise<ReassignEntryResponse> {
    return this.http.post(
      `${this.prefix}/providers/${providerType}/models/reassign`,
      params,
    );
  }

  async setProviderParams(
    providerType: string,
    params: Record<string, unknown>,
  ): Promise<void> {
    return this.http.put(`${this.prefix}/providers/${providerType}/params`, {
      params,
    });
  }

  async syncCatalog(): Promise<SyncResult> {
    return this.http.post(`${this.prefix}/providers/sync`, {});
  }

  /**
   * Discover a provider's live model list. With no
   * `modelIds` it PREVIEWS — fetch + return `candidates`, persist nothing. With
   * `modelIds` it IMPORTS only those candidates (by `name`) as
   * origin='discovered' entries and returns the account's `models`. `deviceId`
   * is required only for `lmstudio` (bridge routing); cloud providers ignore it.
   */
  async discoverProviderModels(
    provider: ProviderType,
    opts: { deviceId?: string; modelIds?: string[] } = {},
  ): Promise<DiscoverProviderResult> {
    return this.http.post(`${this.prefix}/providers/${provider}/discover`, {
      device_id: opts.deviceId ?? '',
      model_ids: opts.modelIds ?? [],
    });
  }

  // --- Provider BYOK credentials ---
  // All credential routes are account-admin gated (403 for non-admins). The
  // plaintext key travels only in setProviderCredential's request body; every
  // response carries only presence + masked hint (never the key).

  async getProviderCredentialStatus(
    provider: ProviderType,
  ): Promise<ProviderCredentialStatus> {
    return this.http.get(`${this.prefix}/providers/${provider}/credential`);
  }

  /**
   * Validate (live, against the provider's models endpoint) and store the
   * account's API key for a provider. An invalid key is a 422 carrying the
   * provider's message; an unreachable provider is a 502 — the key is stored
   * on neither. lmstudio (keyless) is always 422.
   */
  async setProviderCredential(
    provider: ProviderType,
    apiKey: string,
  ): Promise<ProviderCredentialStatus> {
    return this.http.put(`${this.prefix}/providers/${provider}/credential`, {
      api_key: apiKey,
    });
  }

  async deleteProviderCredential(
    provider: ProviderType,
  ): Promise<ProviderCredentialStatus> {
    return this.http.delete(`${this.prefix}/providers/${provider}/credential`);
  }

  /** Set the per-provider key-resolution policy (context ADR-032). */
  async setByokMode(
    provider: ProviderType,
    mode: ByokMode,
  ): Promise<ProviderCredentialStatus> {
    return this.http.put(`${this.prefix}/providers/${provider}/byok-mode`, {
      byok_mode: mode,
    });
  }
}
